import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// Jun 2026 (Randy) — per-USER, per-silo collapse memory for chevrons.
// localStorage gives an instant first paint; the server value
// (users.ui_prefs via /me/ui-prefs) is the source of truth so the choice
// survives log-out and follows the user across browsers/devices.
export function useCollapsePref(siloId, key, defaultCollapsed = false) {
  const lsKey = `mysilo:uiprefs:${siloId}:${key}`;
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      const v = window.localStorage.getItem(lsKey);
      return v == null ? defaultCollapsed : v === "1";
    } catch { return defaultCollapsed; }
  });

  // Reconcile with the server on mount / silo change.
  useEffect(() => {
    if (!siloId) return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/me/ui-prefs`, { params: { silo_id: siloId } });
        const val = r.data?.prefs?.[key];
        if (alive && typeof val === "boolean") {
          setCollapsedState(val);
          try { window.localStorage.setItem(lsKey, val ? "1" : "0"); } catch { /* */ }
        }
      } catch { /* offline / unauth — keep local */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siloId, key]);

  const setCollapsed = useCallback((next) => {
    setCollapsedState(next);
    try { window.localStorage.setItem(lsKey, next ? "1" : "0"); } catch { /* */ }
    api.patch(`/me/ui-prefs`, { silo_id: siloId, key, value: next }).catch(() => {});
  }, [siloId, key, lsKey]);

  const toggle = useCallback(() => setCollapsed(!collapsed), [collapsed, setCollapsed]);
  return [collapsed, toggle, setCollapsed];
}
